import React, { useEffect, useState } from 'react';
import AddQuestionButton from './AddQuestionButton';
import theme from '@/constants/theme';
import EditableQuestion from './EditableQuestion';
import update from 'immutability-helper';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Quiz } from '../common';

interface Props {
  quizData: string | null;
  setQuizData: React.Dispatch<React.SetStateAction<string | null>>;
}

const QuizEditor: React.FC<Props> = ({ quizData, setQuizData }) => {
  const [quiz, setQuiz] = useState<Quiz | null>(null);

  const moveQuestion = (fromIndex: number, toIndex: number) => {
    setQuiz((prevQuiz) => {
      return update(prevQuiz, {
        questions: {
          $splice: [
            [fromIndex, 1],
            [toIndex, 0, (prevQuiz ?? { questions: [] }).questions[fromIndex]],
          ],
        },
      });
    });
  };

  // hidrate quiz data at first render
  useEffect(() => {
    if (!quizData) {
      setQuiz({ questions: [] });
      return;
    }
    setQuiz(JSON.parse(quizData));
  }, []);

  // handle safe quiz data with debounce
  useEffect(() => {
    if (!quiz) return;
    const timerId = setTimeout(() => {
      setQuizData(JSON.stringify(quiz));
    }, 350);
    return () => clearTimeout(timerId);
  }, [quiz, setQuizData]);

  if (!quiz) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16, marginTop: 16 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3
          style={{
            fontWeight: 'normal',
            color: theme.colors.text.title,
          }}
        >
          Editor del cuestionario
        </h3>
      </div>
      {quiz.questions.length === 0 && (
        <span style={{ color: theme.colors.text.caption }}>
          No hay preguntas definidas para el cuestionario.
        </span>
      )}

      <DndProvider backend={HTML5Backend}>
        {quiz.questions.map((question, index) => (
          <EditableQuestion
            key={question.id}
            id={question.id}
            type={question.type}
            question={question.question}
            options={question.options ?? []}
            config={question.config ?? {}}
            setQuiz={setQuiz}
            index={index}
            moveQuestion={moveQuestion}
          />
        ))}
      </DndProvider>
      <AddQuestionButton quiz={quiz} setQuiz={setQuiz} />
    </div>
  );
};

export default QuizEditor;
